import { useFragment } from '@apollo/client'
import { DeepPartial } from '@apollo/client/utilities'
import {
  Box,
  Button,
  CollapsableCard,
  Icon,
  List,
  RadioButton,
  Text,
} from '@factorialco/deprecated-design-system'
import { useFormik } from 'formik'
import {
  EngagementPublishSurveyDocument,
  FormsScheduleRecurrenceTypeEnum,
  PublishDialogSurveyFragment,
  PublishDialogSurveyFragmentDoc,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { RecurrenceSelect } from 'modules/engagement_surveys/components/RecurrenceSelect'
import { StartTimeSelect } from 'modules/engagement_surveys/components/StartTimeSelect'
import {
  scheduleStartDateFromUTC,
  scheduleStartDateToUTC,
} from 'modules/engagement_surveys/lib/getScheduleStartDate'
import { validRespondentStrategy } from 'modules/engagement_surveys/lib/validRespondentStrategy'

interface PublishSurveyFormData {
  startDate?: string
  startTime?: string
  recurrenceType: FormsScheduleRecurrenceTypeEnum
  daysToAnswer?: number
  endsAt?: string
}

interface Props {
  surveyId: number
  onClose: () => void
}

export function PublishDialog({ surveyId, onClose }: Props) {
  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()
  const { data: survey } = useFragment({
    fragment: PublishDialogSurveyFragmentDoc,
    fragmentName: 'PublishDialogSurvey',
    from: {
      __typename: 'SurveysSurvey',
      id: surveyId,
    },
  })

  const [publishSurvey] = useMutation(EngagementPublishSurveyDocument, {
    extractorFn: (data) => data.surveysMutations.publishSurveysSurvey,
  })

  const startDate = scheduleStartDateFromUTC(survey.form?.schedule?.startDate)

  const form = useFormik<PublishSurveyFormData>({
    initialValues: {
      startDate: startDate?.format('YYYY-MM-DD') || '',
      startTime: startDate?.format('HH:mm') || '',
      recurrenceType: survey.form?.schedule?.recurrenceType || 'onetime',
      daysToAnswer: survey.form?.schedule?.daysToAnswer || undefined,
      endsAt: survey.form?.endsAt,
    },
    onSubmit: async (values) => {
      // The backend expects the date in UTC, so we need to convert it
      // so it matches the user expectations
      const date = scheduleStartDateToUTC({
        startDate: values.startDate,
        startTime: values.startTime,
      })

      const result = await publishSurvey({
        variables: {
          daysToAnswer: values.daysToAnswer,
          endsAt: values.endsAt,
          recurrenceType: values.recurrenceType,
          startDate: date?.format('YYYY-MM-DD'),
          startTime: date?.format('HH:mm'),
          surveyId,
        },
      })

      const handled = handleResult(result, () => null)

      if (handled.success && handled.data?.survey) {
        onClose()
      }
    },
  })

  const isScheduled = !!form.values.startDate
  const publishErrors = getPublishValidationErrors(survey)

  return (
    <Modal
      title={i18n.t('surveys.publish_dialog.title')}
      illustration="surveys"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={() => form.handleSubmit()}
            type="primary"
            loading={form.isSubmitting}
            disabled={publishErrors.length > 0}
          >
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <Text.Normal size="200" weight="semibold">
          {i18n.t('surveys.configuration.publish_settings.title')}
        </Text.Normal>
        <CollapsableCard
          open={false}
          Content={<></>}
          Card={
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal weight="medium">
                {i18n.t('surveys.publish_dialog.launch_now')}
              </Text.Normal>

              <RadioButton
                size="medium"
                value={i18n.t('surveys.publish_dialog.launch_now')}
                onChange={() => form.setFieldValue('startDate', undefined)}
                checked={!isScheduled}
              />
            </Box>
          }
        />
        <CollapsableCard
          open={isScheduled}
          Card={
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal weight="medium">{i18n.t('surveys.publish_dialog.schedule')}</Text.Normal>

              <RadioButton
                size="medium"
                value={i18n.t('surveys.publish_dialog.schedule')}
                onChange={() => form.setFieldValue('startDate', moment().format('YYYY-MM-DD'))}
                checked={isScheduled}
              />
            </Box>
          }
          Content={
            <Box gap="s32">
              <Box flexDirection="row" alignItems="center" gap="s32" width="full">
                <DayPicker
                  withNull
                  label={i18n.t('forms.schedule.start_date')}
                  value={form.values.startDate}
                  onChange={(startDate) => {
                    form.setFieldValue('startDate', startDate)
                  }}
                  error={form.errors.startDate}
                  minDate={new Date()}
                  firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
                  valueFormat="backend"
                />

                <StartTimeSelect
                  value={form.values.startTime}
                  onChange={(startTime) => {
                    form.setFieldValue('startTime', startTime)
                  }}
                  date={form.values.startDate}
                />
              </Box>
            </Box>
          }
        />
        <Box gap="s16">
          <RecurrenceSelect
            value={form.values.recurrenceType}
            onChange={(recurrenceType) => {
              form.setFieldValue('recurrenceType', recurrenceType)
            }}
          />

          <Box flexDirection="row" alignItems="flexStart" gap="s32" width="full">
            <Box gap="s8" width="full">
              <DayPicker
                label={i18n.t('forms.schedule.end_date')}
                value={form.values.endsAt}
                onChange={(endsAt) => {
                  form.setFieldValue('endsAt', endsAt)
                }}
                error={form.errors.endsAt}
                minDate={form.values.startDate ? new Date(form.values.startDate) : new Date()}
                firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
                valueFormat="backend_datetime"
              />
              <Text.Normal size="200" color="grey700">
                {i18n.t('forms.schedule.end_date_description')}
              </Text.Normal>
            </Box>

            {form.values.recurrenceType !== 'onetime' ? (
              <TextField
                label={i18n.t('forms.schedule.days_to_answer')}
                value={
                  form.values.daysToAnswer && isFinite(form.values.daysToAnswer)
                    ? form.values.daysToAnswer?.toString()
                    : ''
                }
                onChange={(value) => {
                  const daysToAnswer = value && isFinite(Number(value)) ? Number(value) : undefined

                  form.setFieldValue('daysToAnswer', daysToAnswer)
                }}
                error={form.errors.daysToAnswer}
              />
            ) : (
              <Box width="full" />
            )}
          </Box>
        </Box>

        {publishErrors.length > 0 && (
          <Box minWidth="s600">
            <List>
              {publishErrors.map((error) => (
                <List.Item key={error}>
                  <Box gap="s16" width="full" flexDirection="row" alignItems="center">
                    <Icon.Medium color="radicalRed1000" icon="cross" />
                    {error}
                  </Box>
                </List.Item>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </Modal>
  )
}

function getPublishValidationErrors(survey: DeepPartial<PublishDialogSurveyFragment>) {
  const errors = []

  if (survey?.form?.elementsConnection?.totalCount === 0) {
    errors.push(i18n.t('surveys.publish_dialog.errors.no_elements'))
  }

  const respondentStrategy = survey?.respondentStrategy

  const respondentStrategyInput = respondentStrategy?.strategy
    ? {
        strategy: respondentStrategy.strategy,
        arguments: respondentStrategy.arguments?.filter((argument) => argument !== undefined) || [],
      }
    : undefined

  if (!respondentStrategyInput || !validRespondentStrategy(respondentStrategyInput)) {
    errors.push(i18n.t('surveys.publish_dialog.errors.no_participants'))
  }

  if (survey.form?.title === i18n.t('surveys.default_title')) {
    errors.push(i18n.t('surveys.publish_dialog.errors.default_title'))
  }

  return errors
}
